import { clsx } from 'clsx';
import moment from 'moment-timezone';
import { Col, Form, Row } from 'antd';
import { useParams } from 'react-router-dom';
import React, { useEffect, useState } from 'react';
import { Edit, EditProps, useGetOne, useNotify, useRedirect, useRefresh } from 'react-admin';

import { axiosErrorToString } from '@utils';
import { financeDepositHttp } from '@network';
import { useEditProps, useRbacEdit, useRecord } from '@hooks';
import { Container, Forbidden, Loader, PageHeader } from '@components';
import {
  CreateFinanceDepositParams,
  FinanceAccount,
  FinanceDeposit,
  FinanceDepositOptions,
} from '@types';
import { FinanceDepositForm } from '@pages/finance-deposit/FinanceDepositForm';
import cls from './finance-deposit-edit.module.css';
import { FinanceHistoryButton } from '@pages/finance-deposit/FinanceHistoryButton';

export const FinanceDepositEdit = (props: EditProps) => {
  const financeDeposit = useRecord<FinanceDeposit>(props);
  const notify = useNotify();
  const redirect = useRedirect();
  const rbacEdit = useRbacEdit();
  const editProps = useEditProps();
  const [formValues, setFormValues] = useState<CreateFinanceDepositParams | null>(null);
  const [options, setOptions] = useState<FinanceDepositOptions | null>(null);
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm<CreateFinanceDepositParams>();
  const [submitted, setSubmitted] = useState(false);
  const [clientName, setClientName] = useState('');

  const Title = ({ record }: {record?: FinanceAccount}) =>
    record ? (<span>Deposit: {record.code}</span>) : null;

  const { id } = useParams();
  const refresh = useRefresh();
  const { data: record } = useGetOne('finance-deposit', { id });

  const getOptions = async () => {
    try {
      const ops: FinanceDepositOptions = await financeDepositHttp.options(id);

      if (!ops) {
        throw new Error('The list of options has not been received');
      }
      setOptions(ops);
    } catch (e) {
      console.error(e);
    }
  };

  const submit = async (params: CreateFinanceDepositParams) => {
    if (loading) return;
    try {
      setLoading(true);
      const model = await financeDepositHttp.update(params, record.id);
      setSubmitted(false);
      addFormData(model);
      notify('Deposit has been updated successfully');
      refresh();
    } catch (e) {
      // @ts-ignore
      notify(axiosErrorToString(e), { type: 'error' });
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const onDelete = async () => {
    try {
      const result = await financeDepositHttp.remove(record.id);

      if (result) {
        notify('Deposit has been deleted');
        redirect('/finance-deposit');
      }
    } catch (e) {
      notify(axiosErrorToString(e));
    }
  };

  const addFormData = (deposit: FinanceDeposit) => {
    setFormValues({
      concept: deposit.concept,
      amount: deposit.amount,
      accountId: deposit.accountId,
      creditId: deposit.creditId,
      paymentDate: moment(deposit.paymentDate).format('DD-MM-YYYY'),
      purpose: deposit.purpose,
      details: deposit.details,
      received: deposit.received,
      isUsd: deposit.isUsd,
      exchangeRate: deposit.exchangeRate,
      amountUsd: deposit.amountUsd,
    });
    deposit.credit && setClientName(deposit.credit.accredited);
  };

  useEffect(() => {
    if (record) {
      addFormData(record);
    }
  }, [record]);

  useEffect(() => {
    (async () => {
      await getOptions();
    })();
  }, []);

  if (!options) {
    return <Loader center />;
  }

  if (!rbacEdit) {
    return <Forbidden />;
  }

  return (
    <Edit {...props} title={<Title />} {...editProps}
      className={clsx(cls.page, 'details-page-wrapper')}>
      <>
        <PageHeader
          title="Deposit"
          linkText="Back to all deposits"
          linkUrl="/finance-deposit"
          right={<FinanceHistoryButton history={financeDeposit?.history || []} />}
        />
        <Container max>
          <Row gutter={24} className={cls.wrapper}>
            <Col xs={24} className={cls.wrapperCol}>
              <div className={cls.block}>
                <div className={cls.blockHeader}>Complete information</div>
                {!formValues ? null : (
                  <Form
                    form={form}
                    layout="horizontal"
                    initialValues={formValues}
                  >
                    <FinanceDepositForm
                      financeDeposit={financeDeposit}
                      loading={loading}
                      setLoading={setLoading}
                      options={options}
                      initial={formValues}
                      onSubmit={submit}
                      form={form}
                      clientName={clientName}
                      setClientName={setClientName}
                      submitted={submitted}
                      setSubmitted={setSubmitted}
                      onDelete={onDelete}
                    />
                  </Form>
                )}
              </div>
            </Col>
          </Row>
        </Container>
      </>
    </Edit>
  );
};
